import { supabase } from './supabaseClient';
import { parseISO, format, isSameDay, addDays } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import debounce from 'lodash/debounce';

/**
 * supabaseService.ts
 * 
 * Core service functions for interacting with Supabase database.
 * 
 * Note: Service data fetching is handled by ServiceContext.tsx
 * This file focuses on appointment scheduling, availability, and helper functions.
 */

// Legacy types - kept for backward compatibility with testimonials/packages/gallery/appointments
export interface Testimonial {
  id: number;
  name: string;
  text: string;
  rating: number;
  date: string;
  service?: string;
}

export interface Package {
  id: number;
  name: string;
  description: string;
  price: string;
  duration: string;
  services: string[];
  image_url?: string;
}

export interface GalleryImage {
  id: number;
  url: string;
  alt: string;
  category: string;
}

export interface Appointment {
  id: number;
  user_id: string;
  service_id: number;
  staff_id?: number;
  date: string;
  start_time: string;
  end_time: string;
  status: 'booked' | 'completed' | 'cancelled';
  notes?: string;
}

// Testimonials
export async function getTestimonials() {
  const { data, error } = await supabase
    .from('testimonials')
    .select('*')
    .order('date', { ascending: false });
    
  if (error) {
    console.error('Error fetching testimonials:', error);
    return [];
  }
  
  return data as Testimonial[];
}

// Packages
export async function getPackages() {
  const { data, error } = await supabase
    .from('packages')
    .select('*')
    .order('name');
    
  if (error) {
    console.error('Error fetching packages:', error);
    return [];
  }
  
  return data as Package[];
}

// Gallery
export async function getGalleryImages(category?: string) {
  let query = supabase
    .from('gallery')
    .select('*');
    
  if (category) {
    query = query.eq('category', category);
  }
  
  const { data, error } = await query.order('id');
    
  if (error) {
    console.error('Error fetching gallery images:', error);
    return [];
  }
  
  return data as GalleryImage[];
}

// Appointments
export async function getUserAppointments(userId: string) {
  const { data, error } = await supabase
    .from('appointments')
    .select(`
      *,
      services:service_id(id, name, duration, price)
    `)
    .eq('user_id', userId)
    .order('date', { ascending: true });
    
  if (error) {
    console.error('Error fetching user appointments:', error);
    return [];
  }
  
  return data;
}

export async function createAppointment(appointment: Omit<Appointment, 'id'>) {
  const { data, error } = await supabase
    .from('appointments')
    .insert(appointment)
    .select()
    .single();
    
  if (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
  
  return data;
}

export async function updateAppointment(id: number, updates: Partial<Appointment>) {
  const { data, error } = await supabase
    .from('appointments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
    
  if (error) {
    console.error(`Error updating appointment ${id}:`, error);
    throw error;
  }
  
  return data;
}

export async function cancelAppointment(id: number) {
  return updateAppointment(id, { status: 'cancelled' });
}

// User profile
export async function getUserProfile(userId: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
    
  if (error) {
    console.error(`Error fetching user profile for ${userId}:`, error);
    return null;
  }
  
  return data;
}

export async function updateUserProfile(userId: string, updates: any) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
    
  if (error) {
    console.error(`Error updating user profile for ${userId}:`, error);
    throw error;
  }
  
  return data;
}

// For admin content management
export async function getContent(type: string) {
  const { data, error } = await supabase
    .from('content')
    .select('*')
    .eq('type', type);
    
  if (error) {
    console.error(`Error fetching content of type ${type}:`, error);
    return null;
  }
  
  return data;
}

export async function updateContent(id: number, updates: any) {
  const { data, error } = await supabase
    .from('content')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
    
  if (error) {
    console.error(`Error updating content ${id}:`, error);
    throw error;
  }
  
  return data;
}

// Simple in-memory cache for time slots
const timeSlotCache: {
  [key: string]: {
    slots: string[];
    timestamp: number;
    expiryMs: number;
  }
} = {};

/**
 * Clear all cached time slots. Call after cancelling an appointment
 * to ensure fresh data on the next fetch.
 */
export function clearTimeSlotCache() {
  for (const key of Object.keys(timeSlotCache)) {
    delete timeSlotCache[key];
  }
}

// CONSTANT FOR TIMEZONE
const TEXAS_TIMEZONE = 'America/Chicago';

// Buffer time between different customers' appointments (in minutes)
// This gives staff time to prepare between appointments
const APPOINTMENT_BUFFER_MINUTES = 15;

/**
 * Get available time slots for a staff member on a given date, considering total duration needed (in minutes).
 * Returns an array of time strings (e.g., ['09:00:00', '09:30:00', ...])
 *
 * Logic:
 * 1. Get recurring availability for day of week (is_recurring=true) → working hours
 * 2. If no recurring availability → staff doesn't work this day, return empty
 * 3. Fetch blocked times for specific date (is_recurring=false) → subtract from working hours
 * 4. Fetch booked appointments → subtract from available time
 * 5. Generate slots within available hours, excluding blocked times and bookings
 */
export async function getAvailableTimeSlotsForStaff(
  staffId: string,
  date: Date,
  totalDuration: number,
  forceRefresh = false,
  allowPastSlots = false
): Promise<string[]> {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const cacheKey = `${staffId}-${dateStr}-${totalDuration}-${allowPastSlots ? 'past' : 'nopast'}`;
    const cacheExpiryTime = 5 * 60 * 1000; // 5 minutes cache

    // Return cached data if available and not expired
    if (
      !forceRefresh &&
      timeSlotCache[cacheKey] &&
      Date.now() - timeSlotCache[cacheKey].timestamp < timeSlotCache[cacheKey].expiryMs
    ) {
      console.log(`[getAvailableTimeSlotsForStaff] Using cached data for ${cacheKey}`);
      return timeSlotCache[cacheKey].slots;
    }

    // Check if totalDuration is valid
    if (totalDuration <= 0) {
      console.warn("[getAvailableTimeSlotsForStaff] Invalid duration, must be > 0");
      return [];
    }

    const slotInterval = 15; // minutes

    // 1. Get recurring availability for this day of week (working hours)
    const { data: recurringAvailability, error: recurringError } = await supabase
      .from('staff_availability')
      .select('start_time, end_time')
      .eq('staff_id', staffId)
      .eq('day_of_week', dayOfWeek)
      .eq('is_recurring', true);

    if (recurringError) {
      console.error('[getAvailableTimeSlotsForStaff] Error fetching recurring availability:', recurringError);
    }

    // 2. If no recurring availability, staff doesn't work this day
    if (!recurringAvailability || recurringAvailability.length === 0) {
      console.log(`[getAvailableTimeSlotsForStaff] No recurring availability for staff ${staffId} on day ${dayOfWeek}`);
      timeSlotCache[cacheKey] = {
        slots: [],
        timestamp: Date.now(),
        expiryMs: cacheExpiryTime
      };
      return [];
    }

    const availableRanges = recurringAvailability.map(a => ({
      startTime: a.start_time.substring(0, 5), // Convert HH:mm:ss to HH:mm
      endTime: a.end_time.substring(0, 5),
    }));
    console.log(`[getAvailableTimeSlotsForStaff] Recurring availability for day ${dayOfWeek}:`, availableRanges);

    // 3. Fetch blocked times overlapping this date (is_recurring=false = blocked time).
    // Blocks may span multiple calendar days via end_date.
    const { data: blockedTimes, error: blockedError } = await supabase
      .from('staff_availability')
      .select('specific_date, end_date, start_time, end_time')
      .eq('staff_id', staffId)
      .eq('is_recurring', false)
      .lte('specific_date', dateStr)
      .or(`end_date.gte.${dateStr},and(end_date.is.null,specific_date.eq.${dateStr})`);

    if (blockedError) {
      console.error('[getAvailableTimeSlotsForStaff] Error fetching blocked times:', blockedError);
    }

    // 4. Fetch booked appointments for conflict checking
    const { data: bookings, error: bookingsError } = await supabase
      .rpc('get_staff_blocked_times', {
        p_staff_id: staffId,
        p_date: dateStr
      });

    if (bookingsError) {
      console.error('[getAvailableTimeSlotsForStaff] Error fetching staff bookings:', bookingsError);
      return [];
    }

    // Use minutes since midnight for blocked time comparisons (simpler and timezone-safe)
    // All times in the database are stored as Texas local time strings (HH:mm:ss)
    const blockedMinuteRanges: { start: number; end: number }[] = [];

    // Add blocked times from staff_availability (is_recurring=false).
    // For multi-day blocks, clip per-day: if the block started on a previous
    // day, effective start is 00:00; if it ends on a later day, effective end is 23:59.
    if (blockedTimes && blockedTimes.length > 0) {
      console.log(`[getAvailableTimeSlotsForStaff] Found ${blockedTimes.length} blocked time(s) overlapping ${dateStr}`);
      for (const block of blockedTimes as Array<{ specific_date: string; end_date: string | null; start_time: string; end_time: string }>) {
        const blockEndDate = block.end_date ?? block.specific_date;
        const effectiveStart = block.specific_date < dateStr ? '00:00' : block.start_time.substring(0, 5);
        const effectiveEnd = blockEndDate > dateStr ? '23:59' : block.end_time.substring(0, 5);

        const [startH, startM] = effectiveStart.split(':').map(Number);
        const [endH, endM] = effectiveEnd.split(':').map(Number);

        const blockStartMinutes = startH * 60 + startM;
        const blockEndMinutes = endH * 60 + endM;

        console.log(`[getAvailableTimeSlotsForStaff] Blocked time on ${dateStr}: ${effectiveStart} - ${effectiveEnd} (${blockStartMinutes}-${blockEndMinutes} minutes)`);
        blockedMinuteRanges.push({ start: blockStartMinutes, end: blockEndMinutes });
      }
    }

    // Add booked appointments to blocked ranges (with buffer time for staff preparation)
    // These are stored as UTC timestamps, so we need to convert to Texas time
    if (bookings) {
      for (const booking of bookings) {
        if (!booking.start_time || !booking.end_time) continue;

        const startTimeStr = booking.start_time.endsWith('Z') ? booking.start_time : `${booking.start_time}Z`;
        const endTimeStr = booking.end_time.endsWith('Z') ? booking.end_time : `${booking.end_time}Z`;

        // Parse UTC timestamps and convert to Texas time
        const utcBookingStart = parseISO(startTimeStr);
        const utcBookingEnd = parseISO(endTimeStr);
        const txBookingStart = toZonedTime(utcBookingStart, TEXAS_TIMEZONE);
        const txBookingEnd = toZonedTime(utcBookingEnd, TEXAS_TIMEZONE);

        // Extract hours and minutes in Texas time
        const bookingStartMinutes = txBookingStart.getHours() * 60 + txBookingStart.getMinutes();
        const bookingEndMinutes = txBookingEnd.getHours() * 60 + txBookingEnd.getMinutes();

        // Add buffer time after appointment for staff preparation between customers
        const bookingEndWithBuffer = bookingEndMinutes + APPOINTMENT_BUFFER_MINUTES;

        blockedMinuteRanges.push({ start: bookingStartMinutes, end: bookingEndWithBuffer });
      }
    }

    // 5. Generate slots within available ranges, excluding blocked times and bookings
    const availableSlots: string[] = [];

    // Get current time in Texas for filtering past slots
    const nowInTexas = toZonedTime(new Date(), TEXAS_TIMEZONE);
    const nowMinutes = nowInTexas.getHours() * 60 + nowInTexas.getMinutes();
    const isToday = isSameDay(date, new Date());

    // Process each available time range
    for (const range of availableRanges) {
      const [startHour, startMinute] = range.startTime.split(':').map(Number);
      const [endHour, endMinute] = range.endTime.split(':').map(Number);

      // Convert to total minutes for easier iteration
      const rangeStartMinutes = startHour * 60 + startMinute;
      const rangeEndMinutes = endHour * 60 + endMinute;

      // Generate slots within this range
      for (let slotStartMinutes = rangeStartMinutes; slotStartMinutes < rangeEndMinutes; slotStartMinutes += slotInterval) {
        const slotEndMinutes = slotStartMinutes + totalDuration;

        // Skip slots in the past for today
        if (isToday && slotStartMinutes < nowMinutes && !allowPastSlots) {
          continue;
        }

        // Skip slots that end after the available range
        if (slotEndMinutes > rangeEndMinutes) {
          continue;
        }

        // Check if slot overlaps with any blocked range (blocked times + booked appointments)
        let isOverlapped = false;
        for (const blocked of blockedMinuteRanges) {
          if (slotStartMinutes < blocked.end && slotEndMinutes > blocked.start) {
            isOverlapped = true;
            break;
          }
        }

        // If not overlapped, add to available slots
        if (!isOverlapped) {
          const hour = Math.floor(slotStartMinutes / 60);
          const minute = slotStartMinutes % 60;
          const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
          availableSlots.push(timeStr);
        }
      }
    }

    // Sort slots chronologically and remove duplicates
    const uniqueSortedSlots = Array.from(new Set(availableSlots)).sort();

    // Add to cache
    timeSlotCache[cacheKey] = {
      slots: uniqueSortedSlots,
      timestamp: Date.now(),
      expiryMs: cacheExpiryTime
    };

    return uniqueSortedSlots;
  } catch (err) {
    console.error('[getAvailableTimeSlotsForStaff] Unexpected error:', err);
    return [];
  }
}

/**
 * Preload time slots for dates adjacent to the given date to improve perceived performance
 */
export async function preloadAdjacentDates(
  staffId: string,
  date: Date,
  totalDuration: number
): Promise<void> {
  try {
    const tomorrow = addDays(date, 1);
    const yesterday = addDays(date, -1);
    
    // Don't await these - let them load in background
    // Wrap in try/catch to handle errors silently
    try {
      getAvailableTimeSlotsForStaff(staffId, tomorrow, totalDuration);
    } catch (e) {
      console.error('[preloadAdjacentDates] Error preloading tomorrow:', e);
    }
    
    try {
      getAvailableTimeSlotsForStaff(staffId, yesterday, totalDuration);
    } catch (e) {
      console.error('[preloadAdjacentDates] Error preloading yesterday:', e);
    }
  } catch (error) {
    console.error('[preloadAdjacentDates] Error:', error);
    // Silently fail - this is a performance optimization, not critical functionality
  }
}

/**
 * Fetch with retry functionality for better error resilience
 */
export async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries = 3,
  delayMs = 1000
): Promise<T> {
  let retries = 0;
  while (true) {
    try {
      return await fetchFn();
    } catch (error) {
      retries++;
      if (retries >= maxRetries) throw error;
      console.log(`Retry ${retries}/${maxRetries} after ${delayMs}ms...`);
      await new Promise(resolve => setTimeout(resolve, delayMs * retries)); // Exponential backoff
    }
  }
}